import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ShieldCheck, AlertCircle } from "lucide-react";
import Button from "../../../../shared/ui/button/Button";
import { Modal } from "../../../../shared/ui/modal";
import { abandonMfaSignIn, verifyMfaSignIn } from "../../application/signIn";
import { useNavigate } from "react-router";
import { useTherapist } from "../../../../context/useTherapist";

const TOTP_LENGTH = 6;

interface MfaChallengeProps {
  email: string;
  password?: string;
  /** Called once the user backs out of the challenge, after its session is dropped. */
  onCancel: () => void;
}

/**
 * The second-factor step of a sign-in, as a dialog over the sign-in page.
 *
 * Its first control is the code field, so the dialog opens with it focused
 * and a phone brings up its numeric keypad straight away.
 */
export default function MfaChallenge({ email, password, onCancel }: MfaChallengeProps) {
  const { t } = useTranslation();
  const [totpCode, setTotpCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { setTherapist, refreshTherapist } = useTherapist();
  const navigate = useNavigate();

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);

    try {
      // Firebase sign-in, then the code, then the refreshed token.
      const therapist = await verifyMfaSignIn(totpCode, email, password);

      setTherapist(therapist);
      refreshTherapist();

      navigate("/");
    } catch (err: unknown) {
      console.error("MFA verification failed:", err);
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      // Too many wrong codes: no code works until the lock runs out.
      setErrorMessage(t(detail === "mfa_locked" ? "auth.errorMfaLocked" : "auth.errorInvalidMfa"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    // A verification in flight may still sign the user in; let it finish.
    if (loading) return;
    try {
      await abandonMfaSignIn();
    } catch (err: unknown) {
      console.error("Could not drop the MFA-pending session:", err);
    }
    onCancel();
  };

  const handleCodeChange = (value: string) => {
    // Digits only: a pasted "123 456" or an autofilled code with a stray
    // character would otherwise never reach six valid characters.
    setTotpCode(value.replace(/\D/g, "").slice(0, TOTP_LENGTH));
  };

  return (
    <Modal
      isOpen
      onClose={handleCancel}
      showCloseButton={false}
      ariaLabel={t("auth.mfaTitle")}
      className="max-w-md overflow-hidden rounded-3xl"
    >
      <form onSubmit={handleVerify} className="flex flex-col gap-6 p-6 sm:p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-400">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <div>
            <h2 className="mb-2 text-2xl font-bold text-content-primary">
              {t("auth.mfaTitle")}
            </h2>
            <p className="text-sm text-content-tertiary">
              {t("auth.mfaSubtitle")}
            </p>
          </div>
        </div>

        {errorMessage && (
          <motion.div
            role="alert"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 rounded-2xl bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm flex gap-3 items-center"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {errorMessage}
          </motion.div>
        )}

        <div className="space-y-2">
          <label htmlFor="totpCode" className="sr-only">
            {t("auth.totpCode")}
          </label>
          {/* Large, centred digits: well past the 16px below which iOS zooms
              the page on focus. */}
          <input
            id="totpCode"
            name="totpCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={TOTP_LENGTH}
            placeholder="000000"
            value={totpCode}
            onChange={(e) => handleCodeChange(e.target.value)}
            required
            className="field-base field-default h-14 w-full appearance-none px-4 text-center font-mono text-2xl tracking-[0.5em]"
          />
        </div>

        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            className="w-full"
            loading={loading}
            disabled={totpCode.length < TOTP_LENGTH}
          >
            {t("auth.verifyAndSignIn")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleCancel}
            disabled={loading}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
